import React, { useEffect, useState } from 'react'
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, Line, ComposedChart
} from 'recharts'
import { fetchAllDataAPI } from '../../utils/api'
import { toast } from 'react-toastify'

const DashboardBarChart = () => {
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { getChartData(); }, []);

    const getChartData = () => {
        fetchAllDataAPI('sub-get-dashboard-bar-chart-data')
            .then((res) => {
                if (res.data.status === 1) setChartData(res.data.chartdata);
                else setFallback();
            })
            .catch(() => { toast.warning("Could not load chart data"); setFallback(); })
            .finally(() => setLoading(false));
    }

    const setFallback = () => {
        setChartData([
            { month: 'Oct', orders: 40, amount: 24000, delivered: 28, delivery_pct: 70 },
            { month: 'Nov', orders: 55, amount: 31000, delivered: 42, delivery_pct: 76 },
            { month: 'Dec', orders: 70, amount: 45000, delivered: 58, delivery_pct: 83 },
            { month: 'Jan', orders: 48, amount: 28000, delivered: 35, delivery_pct: 73 },
            { month: 'Feb', orders: 62, amount: 38000, delivered: 50, delivery_pct: 81 },
            { month: 'Mar', orders: 85, amount: 52000, delivered: 71, delivery_pct: 84 },
        ]);
    }

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    background: "#fff", border: "1px solid #eee",
                    borderRadius: "10px", padding: "10px 16px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: "13px"
                }}>
                    <p style={{ fontWeight: 700, marginBottom: "6px", color: "#333" }}>{label}</p>
                    {payload.map((entry, i) => (
                        <p key={i} style={{ color: entry.color, margin: "3px 0" }}>
                            {entry.name === 'amount'
                                ? `💰 Revenue: ₹${entry.value.toLocaleString()}`
                                : entry.name === 'delivery_pct'
                                    ? `🎯 Delivery Rate: ${entry.value}%`
                                    : `📦 Orders: ${entry.value}`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    // ── Total summary values ──────────────────────────────────────
    const totalOrders = chartData.reduce((s, i) => s + i.orders, 0);
    const totalDelivered = chartData.reduce((s, i) => s + (i.delivered || 0), 0);
    const totalAmount = chartData.reduce((s, i) => s + i.amount, 0);
    const avgDeliveryPct = totalOrders > 0 ? Math.round((totalDelivered / totalOrders) * 100) : 0;

    // Color for delivery % badge
    const pctColor = avgDeliveryPct >= 80 ? '#0e7c52' : avgDeliveryPct >= 60 ? '#e65100' : '#c62828';

    return (
        <div className="card radius-10 p-3 w-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0" style={{ fontWeight: 600 }}>📊 Monthly Orders Overview</h6>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                        fontSize: '12px', fontWeight: 700, padding: '3px 10px',
                        borderRadius: '20px', background: pctColor + '18', color: pctColor
                    }}>
                        🎯 Avg Delivery: {avgDeliveryPct}%
                    </span>
                    <span style={{ fontSize: "12px", color: "#888" }}>Last 6 Months</span>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-4">Loading chart...</div>
            ) : (
                <>
                    <ResponsiveContainer width="100%" height={500}>
                        <ComposedChart
                            data={chartData}
                            margin={{ top: 10, right: 50, left: 10, bottom: 5 }}
                            barCategoryGap="30%"
                            barGap={4}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" vertical={false} />

                            <XAxis
                                dataKey="month"
                                tick={{ fontSize: 12, fill: "#666", fontWeight: 500 }}
                                axisLine={false} tickLine={false}
                            />

                            {/* Left Y — Orders */}
                            <YAxis
                                yAxisId="left" orientation="left"
                                tick={{ fontSize: 11, fill: "#4e9191" }}
                                axisLine={false} tickLine={false}
                                label={{ value: 'Orders', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 11, fill: "#4e9191" } }}
                            />

                            {/* Right Y — Revenue */}
                            <YAxis
                                yAxisId="right" orientation="right"
                                tick={{ fontSize: 11, fill: "#c779db" }}
                                axisLine={false} tickLine={false}
                                tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`}
                                label={{ value: 'Revenue', angle: 90, position: 'insideRight', offset: 10, style: { fontSize: 11, fill: "#c779db" } }}
                            />

                            {/* ✅ Third Y — Delivery % (0-100) */}
                            <YAxis
                                yAxisId="pct" orientation="right"
                                tick={{ fontSize: 11, fill: "#0e7c52" }}
                                axisLine={false} tickLine={false}
                                tickFormatter={(v) => `${v}%`}
                                domain={[0, 100]}
                                hide={true}
                            />

                            {/* ✅ Make pct axis visible and properly scaled */}
                            <YAxis
                                yAxisId="pct"
                                orientation="right"
                                tick={{ fontSize: 11, fill: "#0e7c52" }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(v) => `${v}%`}
                                domain={[0, 100]}
                                hide={false}  // ✅ show it
                            />

                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />

                            <Legend
                                wrapperStyle={{ fontSize: "13px", paddingTop: "12px" }}
                                formatter={(value) => (
                                    <span style={{ color: "#555", fontWeight: 500 }}>
                                        {value === 'amount'
                                            ? '💰 Revenue (₹)'
                                            : value === 'delivery_pct'
                                                ? '🎯 Delivery %'
                                                : '📦 Orders'}
                                    </span>
                                )}
                            />

                            {/* Orders bar */}
                            <Bar yAxisId="left" dataKey="orders" name="orders"
                                fill="#1ea8a8" radius={[6, 6, 0, 0]} maxBarSize={25}
                                label={{ position: 'top', fontSize: 10, fill: "#1ea8a8", fontWeight: 600 }}
                            />

                            {/* Revenue bar */}
                            <Bar yAxisId="right" dataKey="amount" name="amount"
                                fill="#c779db" radius={[6, 6, 0, 0]} maxBarSize={25}
                                label={{ position: 'top', fontSize: 10, fill: "#c779db", fontWeight: 600, formatter: (v) => `₹${(v / 1000).toFixed(0)}k` }}
                            />

                            {/* ✅ Delivery % as a Bar instead of Line — same style as others */}
                            <Bar
                                yAxisId="pct"
                                dataKey="delivery_pct"
                                name="delivery_pct"
                                fill="#0e7c52"
                                radius={[6, 6, 0, 0]}
                                maxBarSize={25}
                                label={{
                                    position: 'top',
                                    fontSize: 10,
                                    fill: "#0e7c52",
                                    fontWeight: 700,
                                    formatter: (v) => `${v}%`
                                }}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>

                    {/* Summary Row */}
                    <div className="d-flex justify-content-around mt-2 pt-2"
                        style={{ borderTop: "1px solid #dadada" }}>
                        <div className="text-center">
                            <p style={{ fontSize: "11px", color: "#000", margin: 0 }}>Total Orders</p>
                            <p style={{ fontSize: "16px", fontWeight: 700, color: "#4e9191", margin: 0 }}>
                                {totalOrders}
                            </p>
                        </div>
                        <div className="text-center">
                            <p style={{ fontSize: "11px", color: "#000", margin: 0 }}>Total Revenue</p>
                            <p style={{ fontSize: "16px", fontWeight: 700, color: "#c779db", margin: 0 }}>
                                ₹{totalAmount.toLocaleString()}
                            </p>
                        </div>
                        <div className="text-center">
                            <p style={{ fontSize: "11px", color: "#000", margin: 0 }}>Avg/Order</p>
                            <p style={{ fontSize: "16px", fontWeight: 700, color: "#597e0d", margin: 0 }}>
                                ₹{totalOrders > 0 ? Math.round(totalAmount / totalOrders).toLocaleString() : 0}
                            </p>
                        </div>
                        {/* ✅ New delivery % summary */}
                        <div className="text-center">
                            <p style={{ fontSize: "11px", color: "#000", margin: 0 }}>Delivered</p>
                            <p style={{ fontSize: "16px", fontWeight: 700, color: "#0e7c52", margin: 0 }}>
                                {totalDelivered}
                                <span style={{ fontSize: '11px', marginLeft: '4px', color: pctColor }}>
                                    ({avgDeliveryPct}%)
                                </span>
                            </p>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default DashboardBarChart